
import React, { useState } from 'react';
import { Teacher, normalizeClassName } from '../types';
import { User, Mail, Briefcase, Shield, Key, Save, Camera, CheckCircle, Eye, EyeOff } from 'lucide-react';

interface Props {
  user: Teacher;
  onUpdateUser: (updated: Teacher) => void;
  availableClasses: string[];
}

const ProfileView: React.FC<Props> = ({ user, onUpdateUser, availableClasses }) => {
  const [formData, setFormData] = useState({ ...user });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdateUser(formData);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in pb-10">
      <div className="bg-white dark:bg-slate-900 rounded-[3rem] shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="h-48 bg-gradient-to-r from-indigo-600 to-indigo-900 relative">
          <div className="absolute -bottom-16 left-10 p-2 bg-white dark:bg-slate-950 rounded-[2.5rem] shadow-xl border-4 border-white dark:border-slate-950">
            <div className="w-32 h-32 bg-indigo-100 dark:bg-slate-800 rounded-[2rem] flex items-center justify-center text- indigo-600 font-black text-5xl relative group cursor-pointer overflow-hidden">
               {user.name[0]}
               <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Camera className="text-white" size={24} />
               </div>
            </div>
          </div>
        </div>
        
        <div className="pt-20 p-10">
           <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
              <div>
                <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight">{user.name}</h2>
                <div className="flex items-center gap-3 mt-2">
                   <div className="px-3 py-1 bg-indigo-500/10 rounded-full border border-indigo-500/20">
                      <p className="text-[10px] text-indigo-500 uppercase tracking-widest font-black">{user.subject}</p>
                   </div>
                   <div className="px-3 py-1 bg-green-500/10 rounded-full border border-green-500/20">
                      <p className="text-[10px] text-green-600 uppercase tracking-widest font-black">Aktif</p>
                   </div>
                </div>
              </div>
              
              {showSuccess && (
                <div className="px-6 py-3 bg-green-500 text-white font-black rounded-2xl flex items-center gap-3 animate-fade-in shadow-xl shadow-green-500/20">
                   <CheckCircle size={18} /> Profil Diperbarui!
                </div>
              )}
           </div>

           <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                 <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-4">Informasi Dasar</p>
                 
                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1 flex items-center gap-2"><User size={12}/> Nama Lengkap</label>
                    <input 
                      type="text" 
                      value={formData.name} 
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border rounded-2xl outline-none font-bold dark:text-white"
                    />
                 </div>

                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1 flex items-center gap-2"><Briefcase size={12}/> Mata Pelajaran Utama</label>
                    <input 
                      type="text" 
                      value={formData.subject} 
                      readOnly
                      className="w-full px-5 py-4 bg-slate-100 dark:bg-slate-800/50 border rounded-2xl outline-none font-bold text-slate-400 cursor-not-allowed"
                    />
                 </div>
                 
                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Penempatan Kelas</label>
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from(new Set((availableClasses || []).map(c => normalizeClassName(c)).filter(Boolean))).map(cls => {
                        const assignedNorm = (formData.assignedClasses || []).map(c => normalizeClassName(c));
                        const isSelected = assignedNorm.includes(cls);
                        return (
                          <button
                            key={cls}
                            type="button"
                            onClick={() => {
                              const current = formData.assignedClasses || [];
                              let updated: string[];
                              if (isSelected) {
                                updated = current.filter(c => normalizeClassName(c) !== cls);
                              } else {
                                updated = [...current, cls];
                              }
                              const cleanUpdated = Array.from(new Set(updated.map(c => normalizeClassName(c)).filter(Boolean)))
                                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
                              setFormData({...formData, assignedClasses: cleanUpdated});
                            }}
                            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                              isSelected
                                ? 'bg-indigo-600 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                            }`}
                          >
                            {cls}
                          </button>
                        );
                      })}
                    </div>
                 </div>
              </div>

              <div className="space-y-6">
                 <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-4">Keamanan & Akun</p>
                 
                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1 flex items-center gap-2"><Shield size={12}/> Username Login</label>
                    <input 
                      type="text" 
                      value={formData.username} 
                      readOnly
                      className="w-full px-5 py-4 bg-slate-100 dark:bg-slate-800/50 border rounded-2xl outline-none font-bold text-slate-400 cursor-not-allowed"
                    />
                 </div>

                 <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1 flex items-center gap-2"><Key size={12}/> Password Baru</label>
                    <input 
                      type="password" 
                      value={formData.password} 
                      onChange={e => setFormData({...formData, password: e.target.value})}
                      className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border rounded-2xl outline-none font-bold dark:text-white"
                      placeholder="Masukkan password baru untuk mengubah"
                    />
                 </div>

                 <div className="md:col-span-2 pt-4">
                    <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-4">Pengaturan Tampilan</p>
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-2xl ${formData.GradeVisibility !== false ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-200 text-slate-500'}`}>
                            {formData.GradeVisibility !== false ? <Eye size={20} /> : <EyeOff size={20} />}
                          </div>
                          <div>
                            <p className="text-sm font-black text-slate-800 dark:text-white">Visibilitas Nilai Tumbuh/Sumatif</p>
                            <p className="text-[10px] font-bold text-slate-500">Tampilkan nilai tugas & sumatif di halaman siswa</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, GradeVisibility: formData.GradeVisibility === false ? true : false})}
                          className={`w-14 h-8 rounded-full relative transition-all duration-300 ${formData.GradeVisibility !== false ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                          <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all duration-300 shadow-sm ${formData.GradeVisibility !== false ? 'left-7' : 'left-1'}`} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-2xl ${formData.RaporVisibility !== false ? 'bg-rose-100 text-rose-600' : 'bg-slate-200 text-slate-500'}`}>
                            {formData.RaporVisibility !== false ? <Eye size={20} /> : <EyeOff size={20} />}
                          </div>
                          <div>
                            <p className="text-sm font-black text-slate-800 dark:text-white">Visibilitas Nilai Rapor Akhir</p>
                            <p className="text-[10px] font-bold text-slate-500">Tampilkan nilai rapor akhir di halaman siswa</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, RaporVisibility: formData.RaporVisibility === false ? true : false})}
                          className={`w-14 h-8 rounded-full relative transition-all duration-300 ${formData.RaporVisibility !== false ? 'bg-rose-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                          <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all duration-300 shadow-sm ${formData.RaporVisibility !== false ? 'left-7' : 'left-1'}`} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-2xl ${formData.SasVisibility !== false ? 'bg-amber-100 text-amber-600' : 'bg-slate-200 text-slate-500'}`}>
                            {formData.SasVisibility !== false ? <Eye size={20} /> : <EyeOff size={20} />}
                          </div>
                          <div>
                            <p className="text-sm font-black text-slate-800 dark:text-white">Visibilitas Nilai S. Akhir</p>
                            <p className="text-[10px] font-bold text-slate-500">Tampilkan nilai sumatif akhir (SAS) di halaman siswa</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, SasVisibility: formData.SasVisibility === false ? true : false})}
                          className={`w-14 h-8 rounded-full relative transition-all duration-300 ${formData.SasVisibility !== false ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                          <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all duration-300 shadow-sm ${formData.SasVisibility !== false ? 'left-7' : 'left-1'}`} />
                        </button>
                      </div>
                    </div>
                 </div>
              </div>

              <div className="md:col-span-2 pt-8">
                 <button type="submit" className="flex items-center justify-center gap-3 px-10 py-5 bg-indigo-600 text-white font-black rounded-3xl hover:bg-indigo-700 shadow-2xl shadow-indigo-500/30 active:scale-95 transition-all uppercase tracking-widest text-sm w-full md:w-auto">
                    <Save size={20} /> Simpan Perubahan Profil
                 </button>
              </div>
           </form>
        </div>
      </div>

      <div className="bg-slate-50 dark:bg-slate-900/50 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800">
         <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 text-amber-600 rounded-2xl"><Shield size={24}/></div>
            <div>
               <p className="text-sm font-black text-slate-800 dark:text-white mb-1">Catatan Keamanan</p>
               <p className="text-xs text-slate-500 leading-relaxed">Anda hanya dapat mengubah Nama dan Password. Untuk perubahan Username atau Mata Pelajaran Utama, harap hubungi Super Admin sekolah melalui bagian Tata Usaha.</p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default ProfileView;
